import React, {useRef, useEffect, useState} from "react";
import FormField from "../../forms/form-field";
import BaseModal from "../../modals/base-modal";
import useLocationStore from "../config/use-location-store";
import FormSelect from "../../forms/form-select";
import Tooltip from "../../tooltip/tooltip";
import {instanceMob} from "../../../utils/utils";
import CreateProviderModal from "./create-provider-modal";
import InlineModal from "../../info-modal/infoModal";
import {getBackendError} from "../../../utils/error-helpers";

const PurchaseModal = ({show, onClose}) => {
    const formRef = useRef(null);
    const [infoModal, setInfoModal] = useState(null);
    const [userData, setUserData] = useState(null);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [providers, setProviders] = useState([]);
    const [nit, setNit] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateProviderModal, setShowCreateProviderModal] = useState(false);
    const [purchaseNumber, setPurchaseNumber] = useState("");

    const [totalValue, setTotalValue] = useState(0);
    const [paidValue, setPaidValue] = useState(0);
    const [pendingValue, setPendingValue] = useState(0);

    const selectedLocation = useLocationStore((state) => state.selectedLocation);

    const handleTotalChange = (val) => {
        const numeric = Number(val) || 0;
        setTotalValue(numeric);
        setPendingValue(numeric - paidValue);
    };

    const handlePaidChange = (val) => {
        const numeric = Number(val) || 0;
        setPaidValue(numeric);
        setPendingValue(totalValue - numeric);
    };

    const handlePaymentChange = (e) => {
        const val = e?.target?.value ?? e;
        const num = Number(val);
        setPaymentMethod(num);

        const form = formRef.current;
        if (form && num !== 50) {
            if (form.elements["other_payment_method_display"]) form.elements["other_payment_method_display"].value = "";
            if (form.elements["other_payment_method"]) form.elements["other_payment_method"].value = "";
        }
    };

    const handleOtherPaymentDisplayChange = (e) => {
        const form = formRef.current;
        if (!form) return;
        const hidden = form.elements["other_payment_method"];
        if (hidden) hidden.value = e.target.value;
    };

    const handleOpenCreateProviderModal = () => {
        setShowCreateProviderModal(true);
    };

    const handleProviderChange = (e) => {
        const selectedValue = e.target.value;
        setSelectedProvider(selectedValue);

        const selected = providers.find((p) => p.value === selectedValue);
        setNit(selected?.nit || "");
    };

    const handleValueChange = () => {
        const form = formRef.current;
        if (!form) return;

        const total = Number(form.querySelector('input[name="value_purchase"][type="hidden"]')?.value || 0);
        const paid = Number(form.querySelector('input[name="value_paid"][type="hidden"]')?.value || 0);

        setPendingValue(0);
    };

    const formFields = [
        {
            Component: FormField,
            label: "Fecha",
            name: "purchaseDate",
            type: "date",
            value: new Date().toISOString().split("T")[0],
            readOnly: true,
            placeholder: "Fecha automática",
            width: "65%",
            isLoading,
        },
        {
            Component: FormField,
            label: "# Registro",
            name: "registry_number",
            type: "text",
            value: purchaseNumber,
            placeholder: "Registro automático",
            readOnly: true,
            isLoading,
        },
        {
            Component: FormField,
            label: "Usuario que Registra",
            name: "registeredByUser",
            type: "text",
            value: userData
                ? `${userData.first_name} ${userData.last_name} (${userData.email})`
                : "",
            placeholder: "Usuario predeterminado",
            readOnly: true,
            isLoading,
        },
        {
            Component: FormField,
            label: "Locación",
            name: "location_id",
            type: "text",
            value: selectedLocation?.name || "",
            placeholder: "Locación predeterminada",
            readOnly: true,
            isLoading,
        },
        {
            Component: FormSelect,
            label: "Nombre",
            name: "provider_id",
            options: providers,
            type: "text",
            placeholder: "Nombre de la empresa",
            searchable: true,
            isLoading,
            actionButton: handleOpenCreateProviderModal,
            required: true,
            value: selectedProvider || "",
            onChange: handleProviderChange,
            tooltip: "Indica el nombre del proveedor o empresa a la que se realiza el pago."
        },
        {
            Component: FormField,
            label: "NIT",
            name: "nit",
            type: "text",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            readOnly: true,
            value: nit
        },
        {
            Component: FormField,
            label: "Descripción de compra",
            name: "purchase_description",
            type: "text",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            tooltip: "Especifica brevemente el producto o servicio adquirido."
        },
        {
            Component: FormField,
            label: "Valor de la compra",
            name: "value_purchase",
            type: "currency",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            value: totalValue,
            onChange: (e) => handleTotalChange(e.target.value),
            tooltip: "Monto total pagado por el producto o servicio."
        },
        {
            Component: FormField,
            label: "Valor pagado",
            name: "value_paid",
            type: "currency",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            value: paidValue,
            onChange: (e) => handlePaidChange(e.target.value),
            tooltip: "Cantidad efectivamente entregada al proveedor."
        },
        {
            Component: FormField,
            label: "Valor pendiente por pagar",
            name: "pendingValue",
            type: "currency",
            placeholder: "valor automático",
            readOnly: true,
            isLoading,
            value: pendingValue || 0,
            tooltip: "Saldo restante si el pago no cubre el total de la compra."
        },
        {
            Component: FormSelect,
            label: "Método de pago",
            name: "payment_method",
            options: [
                {value: 10, label: "Tarjeta de Crédito o Débito"},
                {value: 20, label: "Transferencia"},
                {value: 30, label: "Efectivo"},
                {value: 40, label: "No registra"},
                {value: 50, label: "Otros"},
            ],
            placeholder: "Selecciona una opción",
            tooltip: "Selecciona cómo se realizará el pago.",
            onChange: handlePaymentChange,
            isLoading,
            required: true,
        },
        {
            Component: FormField,
            label: "Entidad",
            name: "other_payment_method_display",
            placeholder: "Escribe el nombre de la entidad",
            tooltip: "Especifica la plataforma usada: Nequi, Daviplata, Efecty u otra.",
            disabled: paymentMethod !== 50,
            onChange: handleOtherPaymentDisplayChange,
            isLoading,
            required: paymentMethod === 50,
        },
    ];

    const handleCloseCreateProviderModal = (newProvider) => {
        setShowCreateProviderModal(false);

        if (newProvider?.provider_id) {
            const formatted = {
                value: String(newProvider.provider_id),
                label: newProvider.name,
                nit: newProvider.nit,
            };

            setProviders((prev) => [...prev, formatted]);
            setSelectedProvider(formatted.value);
            setNit(formatted.nit);
        }
    };

    const fetchProviders = () => {
        setIsLoading(true);
        instanceMob
            .get(`admin/accounting/provider/`, {
                params: {location: selectedLocation.id},
            })
            .then((res) => res.data)
            .then((data) => {
                const formatted = data?.map((p) => ({
                    value: String(p?.provider_id),
                    label: p?.name,
                    nit: p?.nit,
                }));
                setProviders(formatted);
            })
            .catch((err) => console.error("Error cargando proveedores:", err))
            .finally(() => {
                setIsLoading(false);
            });
    };

    const fetchPurchaseNumber = () => {
        instanceMob
            .get(`admin/accounting/register-number?location_id=${selectedLocation.id}`)
            .then((res) => setPurchaseNumber(res.data?.next_registry_number || ""))
            .catch((err) => console.error("Error obteniendo número de registro:", err));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(formRef.current);
        const payload = Object.fromEntries(formData.entries());

        setIsLoading(true);

        instanceMob
            .post("admin/accounting/purchase/create", payload)
            .then((res) => {
                setInfoModal({
                    iconType: "success",
                    title: "Recibo creado",
                    description: "El recibo fue registrado con éxito.",
                    primaryAction: {
                        label: "Aceptar",
                        onClick: () => {
                            setInfoModal(null);
                            onClose();
                        },
                    },
                });
            })
            .catch((err) => {
                const errorMessage = getBackendError(err);
                setInfoModal({
                    iconType: "error",
                    title: "Error",
                    description: errorMessage,
                    secondaryAction: {
                        label: "Cerrar",
                        onClick: () => setInfoModal(null),
                    },
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        const storedUser = {
            email: localStorage.getItem("email"),
            first_name: localStorage.getItem("first_name"),
            last_name: localStorage.getItem("last_name"),
            id: localStorage.getItem("id"),
        };
        setUserData(storedUser);
    }, []);

    useEffect(() => {
        fetchProviders();
        fetchPurchaseNumber();
    }, []);

    useEffect(() => {
        const form = formRef.current;
        if (!form) return;
        if (paymentMethod !== 50) {
            if (form.elements["other_payment_method_display"]) form.elements["other_payment_method_display"].value = "";
            if (form.elements["other_payment_method"]) form.elements["other_payment_method"].value = "";
        }
    }, [paymentMethod]);

    return (
        <>
            <BaseModal
                show={show}
                onClose={onClose}
                title="Gastos"
                onSave={() => formRef.current.requestSubmit()}
            >
                <form
                    className="d-flex flex-column gap-2"
                    ref={formRef}
                    onSubmit={handleSubmit}
                >
                    {formFields.map((field, index) => {
                        const FieldComponent =
                            typeof field.Component === "string"
                                ? React.createElement(field.Component, {...field})
                                : <field.Component {...field} />;

                        const isHidden = field.type === "hidden";

                        if (!isHidden && field.tooltip) {
                            return (
                                <Tooltip key={index} title={field.label} description={field.tooltip} position="right"
                                         variant="rich">
                                    {FieldComponent}
                                </Tooltip>
                            );
                        }
                        return <React.Fragment key={index}>{FieldComponent}</React.Fragment>;
                    })}

                    <input type="hidden" name="other_payment_method"/>
                    <input type="hidden" name="location_id" value={selectedLocation?.id || ""}/>
                </form>
            </BaseModal>

            <CreateProviderModal
                show={showCreateProviderModal}
                onClose={handleCloseCreateProviderModal}
            />

            {infoModal && (
                <InlineModal
                    icon={infoModal.icon}
                    iconType={infoModal.iconType}
                    title={infoModal.title}
                    description={infoModal.description}
                    primaryAction={infoModal.primaryAction}
                    secondaryAction={infoModal.secondaryAction}
                />
            )}
        </>
    );
};

export default PurchaseModal;
